import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Download, RefreshCw, TrendingUp, Users, ClipboardCheck, ChevronDown, FileDown } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { downloadExport } from "@/lib/downloadExport";
import ExportColumnsDialog from "@/components/leads/ExportColumnsDialog";
import { useDispositions } from "@/hooks/useDispositions";
import { toast } from "sonner";

export default function LeadsReports() {
  const { active } = useClient();
  const { byValue: dispositionByValue } = useDispositions();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leads/reports");
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load reports");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const exportReport = async (format) => {
    try {
      const stamp = new Date().toISOString().slice(0, 10);
      await downloadExport("/leads/export", { format },
        `${active?.slug || "leads"}-report-${stamp}.${format}`);
      toast.success(`Report export downloaded (.${format})`);
    } catch {
      toast.error("Export failed");
    }
  };

  if (loading || !data) {
    return <div className="text-slate-400 p-6 text-sm" data-testid="reports-loading">Loading…</div>;
  }

  const kpis = [
    { label: "Total Leads", value: data.total, icon: Users },
    { label: "Contact Rate", value: `${data.contact_rate}%`, icon: TrendingUp },
    { label: "Completion Rate", value: `${data.completion_rate}%`, icon: ClipboardCheck },
    { label: "HQ Capture", value: `${data.capture_rate.head_office}%` },
    { label: "Sector Capture", value: `${data.capture_rate.sector}%` },
    { label: "Emp Count Capture", value: `${data.capture_rate.employee_count}%` },
  ];

  return (
    <div className="space-y-4" data-testid="leads-reports">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-400">Reports</div>
          <h1 className="font-heading text-lg font-semibold text-slate-900">{active?.name} · Analytics</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} className="h-8">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
          <ExportColumnsDialog />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" className="h-8 bg-indigo-600 hover:bg-indigo-700" data-testid="reports-export">
                <Download className="w-3.5 h-3.5 mr-1.5" /> Export
                <ChevronDown className="w-3 h-3 ml-1 opacity-70" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => exportReport("csv")} data-testid="reports-export-csv">
                <FileDown className="w-3.5 h-3.5 mr-2 text-sky-600" /> Export CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportReport("xlsx")} data-testid="reports-export-xlsx">
                <FileDown className="w-3.5 h-3.5 mr-2 text-emerald-600" /> Export Excel
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {kpis.map((k) => (
          <Card key={k.label} className="p-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">{k.label}</div>
            <div className="text-2xl font-semibold text-slate-900">{k.value}</div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Dispositions</CardTitle></CardHeader>
          <CardContent className="p-2">
            <Table className="text-xs">
              <TableHeader><TableRow><TableHead>Disposition</TableHead><TableHead className="text-right">Count</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.disposition.map((d) => {
                  const meta = dispositionByValue[d.code];
                  return (
                    <TableRow key={d.code}>
                      <TableCell>
                        <span className="inline-flex items-center gap-2">
                          <span className="inline-block w-2 h-2 rounded-full"
                                style={{ backgroundColor: meta?.color || "#94A3B8" }} />
                          <Badge variant="outline" className="font-mono text-[10px]">{d.code}</Badge>
                          {meta?.label && meta.label !== d.code && (
                            <span className="text-slate-500">{meta.label}</span>
                          )}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-mono">{d.count}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Attempts (Buckets)</CardTitle></CardHeader>
          <CardContent className="p-2">
            <Table className="text-xs">
              <TableHeader><TableRow><TableHead>Bucket</TableHead><TableHead className="text-right">Count</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.attempts.map((d) => (
                  <TableRow key={d.bucket}>
                    <TableCell className="font-mono text-[10px]">{d.bucket}</TableCell>
                    <TableCell className="text-right font-mono">{d.count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Agent Productivity</CardTitle></CardHeader>
        <CardContent className="p-2">
          {data.productivity.length === 0 ? (
            <div className="text-xs text-slate-400 p-2">No leads have been assigned to agents yet.</div>
          ) : (
            <Table className="text-xs">
              <TableHeader><TableRow><TableHead>Agent</TableHead><TableHead className="text-right">Leads Owned</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.productivity.slice(0, 25).map((p) => (
                  <TableRow key={p.agent}>
                    <TableCell className="font-mono text-[11px]">{p.agent}</TableCell>
                    <TableCell className="text-right font-mono">{p.leads}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
